<template>
  <div v-if="show" class="candidate-description-container" :class="hasResumeView ? 'has-resume-view' : ''">
    <WrappedForm
      ref="form"
      labelWidth="200px"
      class="candidate-description"
      :model="stepData"
      :formLines="formLines"
    >
      <template #source>
        <div class="source-code-container">
          <div class="input source-input">
            <template v-if="canAddSourceCode">
              <el-autocomplete
                v-model="stepData['source']"
                clearable
                :fetch-suggestions="searchSourceList"
                :label="$t(`${i18nPrefix}.label.source`)"
                :placeholder="$t(`${i18nPrefix}.message.sourcePlaceholder`)"
              ></el-autocomplete>
            </template>
            <template v-else>
              <el-select
                v-model="stepData['source']"
                clearable
                filterable
                :placeholder="$t(`${i18nPrefix}.message.sourcePlaceholder`)"
                :filter-method="handleFilterSource"
                @clear="clearSource"
                @visible-change="validateSource"
              >
                <el-option
                  v-for="(option, index) in staticSourceList"
                  :key="index"
                  :label="option.label"
                  :value="option['source'] || option.value"
                />
              </el-select>
            </template>
          </div>
          <div class="tips source-tips">
            <el-tooltip popper-class="tips-content" effect="dark" :content="$t(`${i18nPrefix}.message.sourceTips`)" placement="bottom">
              <div class="tip-icon">?</div>
            </el-tooltip>
          </div>
        </div>
      </template>
      <template #code>
        <div class="source-code-container">
          <div class="input code-input">
            <template v-if="canAddSourceCode">
              <el-autocomplete
                v-model="stepData['code']"
                :fetch-suggestions="searchCodeList"
                clearable
                :label="$t(`${i18nPrefix}.label.code`)"
                :placeholder="$t(`${i18nPrefix}.message.codePlaceholder`)"
              ></el-autocomplete>
            </template>
            <template v-else>
              <el-select
                v-model="stepData['code']"
                clearable
                filterable
                :placeholder="$t(`${i18nPrefix}.message.codePlaceholder`)"
                :filter-method="handleFilterCode"
                @clear="clearCode"
                @visible-change="validateCode"
              >
                <el-option
                  v-for="(option, index) in staticCodeList"
                  :key="index"
                  :label="option.label"
                  :value="option['code'] || option.value"
                />
              </el-select>
            </template>
          </div>
          <div class="tips code-tips">
            <el-tooltip popper-class="tips-content" effect="dark" :content="$t(`${i18nPrefix}.message.codeTips`)" placement="bottom">
              <div class="tip-icon">?</div>
            </el-tooltip>
          </div>
        </div>
      </template>
      <template #isPrivate>
        <div class="is-private">
          <el-switch v-model="stepData.commentIsPrivate" :inactive-text="$t(`${i18nPrefix}.label.isPrivate`)"></el-switch>
        </div>
      </template>
    </WrappedForm>
    <RectLoading v-show="loading" class="loading"></RectLoading>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import mixin from './mixin'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import RectLoading from '@/components/loading/rectLoading.vue'
import { addI18nProperty } from '@/i18n'

export default {
  mixins: [mixin],
  components: {
    WrappedForm,
    RectLoading
  },
  props: {
    hasResumeView: {
      type: Boolean,
      default: false
    },
    setting: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      dataLoader: null,
      canAddSourceCode: false,
      sourceCodeList: [],
      tagList: {
        manual: [],
        smart: []
      },
      stepData: { },
      isSearchSource: false,
      searchedSourceListByKeyword: [],
      isSearchCode: false,
      searchedCodeListByKeyword: []
    }
  },
  computed: {
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    },
    staticSourceList () {
      if (this.canAddSourceCode) {
        return []
      }
      if (this.isSearchSource) {
        return _.map(this.searchedSourceListByKeyword, item => {
          return {
            label: item,
            value: item
          }
        })
      } else {
        const result = []
        _.forEach(this.sourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, sourceInfo => {
            const source = sourceInfo.source
            if (!result.includes(source)) {
              result.push(source)
            }
          })
        })
        return _.map(result, item => {
          return {
            label: item,
            value: item
          }
        })
      }
    },
    staticCodeList () {
      if (this.canAddSourceCode) {
        return []
      }
      if (this.isSearchCode) {
        return _.map(this.searchedCodeListByKeyword, item => {
          return {
            label: item,
            value: item
          }
        })
      } else {
        const result = []
        const source = this.stepData.source
        _.forEach(this.sourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, (sourceInfo) => {
            if (source && sourceInfo.source !== source) {
              return
            }
            _.forEach(sourceInfo.code_list, codeInfo => {
              const code = codeInfo.code
              if (!result.includes(code)) {
                result.push(code)
              }
            })
          })
        })

        return _.map(result, item => {
          return {
            label: item,
            value: item
          }
        })
      }
    },
    hiddenField () {
      return this.setting?.hiddenField || []
    },
    formLines () {
      const vm = this
      const isEditMode = this.isEditMode
      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }
      const lines = {
        source: {
          get hidden () { return hiddenField.includes('source') },
          elType: 'div',
          label: this.$t(`${this.i18nPrefix}.label.source`),
          contentSlot: 'source',
          option: {
            data: this.staticSourceList
          },
          get props () {
            if (vm.canAddSourceCode) {
              return { fetchSuggestions: vm.searchSourceList, clearable: true }
            } else {
              return { filterable: true, clearable: true }
            }
          }
        },
        code: {
          get hidden () { return hiddenField.includes('code') },
          elType: 'div',
          label: this.$t(`${this.i18nPrefix}.label.code`),
          contentSlot: 'code',
          option: {
            data: this.staticCodeList
          },
          get props () {
            if (vm.canAddSourceCode) {
              return { fetchSuggestions: vm.searchCodeList, clearable: true }
            } else {
              return { filterable: true, clearable: true }
            }
          }
        },
        smartTag: {
          hidden: true,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.tag.smart`),
          option: {
            data: this.tagList.smart,
            labelKey: 'name',
            valueKey: 'id'
          },
          props: { clearable: true, filterable: true, multiple: true }
        },
        manualTag: {
          hidden: !this.hasCRMTagPermission,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.tag.manual`),
          option: {
            data: _.chain(this.tagList.manual).map(tag => addI18nProperty(tag, 'i18nName', 'cn_name', 'name')).sortBy('name').value(),
            labelKey: 'i18nName',
            valueKey: 'id'
          },
          props: { clearable: true, filterable: true, multiple: true }
        },
        comment: {
          hidden: isEditMode,
          label: this.$t(`${this.i18nPrefix}.label.comment`),
          props: { type: 'textarea', autosize: { minRows: 5 } },
          contentExtraSlot: 'isPrivate'
        }
      }
      return lines
    }
  },
  created () {

  },
  methods: {
    ...mapActions('sourceCode', ['getCompanySourceCode']),
    ...mapActions('crm', ['searchTag']),
    handleFilterSource (keywords) {
      if (keywords) {
        this.stepData.source = keywords
        let result = []
        _.forEach(this.sourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, sourceInfo => {
            const source = sourceInfo.source
            const searched = keywords ? (source.toUpperCase().includes(keywords.toUpperCase()) || sourceInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
            if (!result.includes(source) && searched) {
              result.push(source)
            }
          })
        })
        result = _.sortBy(result)
        this.searchedSourceListByKeyword = result
        this.isSearchSource = true
      } else {
        this.stepData.source = null
        this.isSearchSource = false
      }
    },
    handleFilterCode (keywords) {
      if (keywords) {
        this.stepData.code = keywords
        let result = []
        const source = this.stepData.source
        _.forEach(this.sourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, (sourceInfo) => {
            if (source && sourceInfo.source !== source) {
              return
            }
            _.forEach(sourceInfo.code_list, codeInfo => {
              const code = codeInfo.code
              const searched = keywords ? (code.toUpperCase().includes(keywords.toUpperCase()) || codeInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
              if (!result.includes(code) && searched) {
                result.push(code)
              }
            })
          })
        })
        result = _.sortBy(result)
        this.searchedCodeListByKeyword = result
        this.isSearchCode = true
      } else {
        this.stepData.code = null
        this.isSearchCode = false
      }
    },
    clearSource () {
      this.stepData.source = null
      this.isSearchSource = false
    },
    clearCode () {
      this.stepData.code = null
      this.isSearchCode = false
    },
    validateSource (show) {
      if (show) {
        this.isSearchSource = false
      } else {
        const hasSource = _.includes(this.searchedSourceListByKeyword, this.stepData.source)
        if (!this.canAddSourceCode && !hasSource && this.isSearchSource) {
          this.$set(this.stepData, 'source', null)
          this.isSearchSource = false
        }
      }
    },
    validateCode (show) {
      if (show) {
        this.isSearchCode = false
      } else {
        const hasCode = _.includes(this.searchedCodeListByKeyword, this.stepData.code)
        if (!this.canAddSourceCode && !hasCode && this.isSearchCode) {
          this.$set(this.stepData, 'code', null)
          this.isSearchCode = false
        }
      }
    },
    async loadData () {
      if (!this.dataLoader) {
        this.dataLoader = Promise.all([
          this.loadSourceCodeList(),
          this.loadTagList()
        ])
      }
      await this.dataLoader
    },
    async loadSourceCodeList () {
      const resp = await this.getCompanySourceCode({ show_agency: 1 })
      if (resp) {
        this.canAddSourceCode = resp.can_add
        this.sourceCodeList = resp.result
      }
    },
    async loadTagList () {
      if (!this.hasCRMTagPermission) {
        return
      }
      const categoryList = ['system', 'smart', 'manual', 'campaign']

      const params = {
        is_company_tag: 1,
        tag_category: categoryList.join(',')
      }
      const resp = await this.searchTag(params)
      if (resp) {
        this.tagList = _.groupBy(resp, 'category')
      }
    },
    searchSourceList (keywords, callback) {
      let result = []
      _.forEach(this.sourceCodeList, ({ source_list: sourceList }) => {
        _.forEach(sourceList, sourceInfo => {
          const source = sourceInfo.source
          const searched = keywords ? (source.toUpperCase().includes(keywords.toUpperCase()) || sourceInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
          if (!result.includes(source) && searched) {
            result.push(source)
          }
        })
      })
      result = _.sortBy(result)
      callback(_.map(result, val => { return { value: val } }))
    },
    searchCodeList (keywords, callback) {
      let result = []
      const source = this.stepData.source
      _.forEach(this.sourceCodeList, ({ source_list: sourceList }) => {
        _.forEach(sourceList, (sourceInfo) => {
          if (source && sourceInfo.source !== source) {
            return
          }
          _.forEach(sourceInfo.code_list, codeInfo => {
            const code = codeInfo.code
            const searched = keywords ? (code.toUpperCase().includes(keywords.toUpperCase()) || codeInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
            if (!result.includes(code) && searched) {
              result.push(code)
            }
          })
        })
      })
      result = _.sortBy(result)
      callback(_.map(result, val => { return { value: val } }))
    },
    async setStepDataToCandidateInfo () {
      const stepData = this.stepData
      const candidateInfo = this.candidateInfo
      this.$set(candidateInfo, 'commentAndTagInfo', {
        source: stepData.source,
        code: stepData.code,
        tags: _.chain(stepData.smartTag).concat(stepData.manualTag).filter().value(),
        comment: stepData.comment,
        comment_is_private: stepData.commentIsPrivate
      })
    },
    async getStepData () {
      await this.loadData()
      const info = this.candidateInfo.commentAndTagInfo || {}
      const tagIds = info.tags || []
      let smartTagList = []
      if (this.tagList.smart) {
        smartTagList = _.concat(this.tagList.smart)
      }
      if (this.tagList.system) {
        smartTagList = _.concat(smartTagList, this.tagList.system)
      }
      if (this.tagList.campaign) {
        smartTagList = _.concat(smartTagList, this.tagList.campaign)
      }
      const stepData = {
        source: info.source,
        code: info.code,
        smartTag: _.chain(smartTagList).filter(item => tagIds.includes(item.id)).map('id').value(),
        manualTag: _.chain(this.tagList.manual).filter(item => tagIds.includes(item.id)).map('id').value(),
        comment: info.comment,
        commentIsPrivate: false
      }
      return stepData
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-description-container {
  position: relative;

  .candidate-description {
    .source-code-container {
      display: flex;

      .input {
        width: 94%;
      }

      .tips {
        width: 5%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 10px;

        .tip-icon {
          cursor: help;
          border: 1px solid #aeaeae;
          color: #aeaeae;
          font-size: 14px;
          flex-shrink: 0;
          font-weight: 400;
          display: flex;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          align-items: center;
          justify-content: center;
        }
      }
    }

    .is-private {
      margin-top: 10px;
      line-height: 20px;

      .el-switch {
        ::v-deep {
          .el-switch__label {
            color: #606266;

            &.is-active {
              color: #606266;
            }
          }
        }
      }
    }
  }

  .loading {
    position: absolute;
  }
}

.has-resume-view {
  .candidate-description {}

  ::v-deep {
    .el-form-item {
      margin-bottom: 18px;
    }
    .el-form-item__label {
      font-size: 12px;
      line-height: 26px !important;
      min-height: 26px !important;
    }
    .label-inner {
      font-size: 12px;
    }

    .el-textarea__inner {
      font-size: 12px;
    }

    .el-input__inner {
      font-size: 12px;
      height: 26px !important;
    }

    .el-form-item__content {
      line-height: 26px !important;
    }

    .is-private {
      .el-switch {
        .el-switch__label {
          span {
            font-size: 12px;
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.tips-content {
  width: 500px;
}
</style>
